import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormControl } from '@angular/forms';
import {
  SharedModule,
  FormInputComponent,
  DynamicFormInputComponent,
  StringCustomFieldConfig,
} from '@vendure/admin-ui/core';
import { HeaderMessageDisplayOrderType, HeaderMessageConfig, HeaderMessageSwitchType, TextValue } from '../ui-types';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';

@Component({
  template: `
    <vdr-card>
      <div class="form-grid">
        <vdr-form-field [label]="'Display Order'">
          <select [formControl]="displayOrderControl">
            <option value="" disabled>Select Message Display Order</option>
            <option value="sequence">Sequence</option>
            <option value="random">Random</option>
          </select>
        </vdr-form-field>

        <vdr-form-field [label]="'Switch Type'">
          <select [formControl]="switchTypeControl">
            <option value="" disabled>Select Switch Type</option>
            <option value="auto">Auto</option>
            <option value="manual">Manual</option>
          </select>
        </vdr-form-field>
      </div>

      <vdr-form-field *ngIf="switchTypeControl.value === 'auto'" [label]="'Auto Switch Interval (seconds)'" class="mt-2">
        <input type="number" min="1" [formControl]="autoSwitchIntervalControl">
      </vdr-form-field>

      <vdr-card class="mt-4">
        <vdr-form-field [label]="'Messages'">
          <vdr-dynamic-form-input
            #messagesInput
            [control]="messagesControl"
            [def]="messageListDef"
          ></vdr-dynamic-form-input>
        </vdr-form-field>
      </vdr-card>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
  styles: [`.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }`],
})
export class HeaderMessageFormComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
  @ViewChild('messagesInput') messagesInput: DynamicFormInputComponent;

  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  headerMessageConfig: HeaderMessageConfig;

  displayOrderControl = new FormControl();
  switchTypeControl = new FormControl();
  autoSwitchIntervalControl = new FormControl();
  messagesControl = new FormControl<string[]>([]);

  private activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;

  messageListDef = {
    list: true,
    type: 'string',
    ui: {
      component: 'text-value-input',
      args: {
        activeLanguageCode: DEFAULT_ACTIVE_LANGUAGE,
        languageChanges$: this.languageChanges$,
      },
    },
  };

  private destroy$ = new Subject<void>();

  constructor(private changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    const defaultHeaderMessageConfig: HeaderMessageConfig = {
      displayOrder: 'sequence',
      switchType: 'auto',
      autoSwitchInterval: 5,
      messageList: [],
    };

    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) {
      this.activeLanguageCode = args.activeLanguageCode;
    }
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
      this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    }
    this.messageListDef = {
      ...this.messageListDef,
      ui: {
        ...this.messageListDef.ui,
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };

    const raw = this.formControl?.value;
    if (raw && typeof raw === 'object') {
      this.headerMessageConfig = {
        ...defaultHeaderMessageConfig,
        ...raw,
        messageList: Array.isArray((raw as any).messageList) ? (raw as any).messageList as TextValue[] : [],
      };
    } else {
      this.headerMessageConfig = defaultHeaderMessageConfig;
      this.formControl.setValue(defaultHeaderMessageConfig);
    }

    this.initializeFormControls();
  }

  ngAfterViewInit() {
    this.displayOrderControl.valueChanges.subscribe(this.onDisplayOrderChange);
    this.switchTypeControl.valueChanges.subscribe(this.onSwitchTypeChange);
    this.autoSwitchIntervalControl.valueChanges.subscribe(this.onAutoSwitchIntervalChange);
    this.messagesControl.valueChanges.subscribe(this.onMessagesChange);

    if (this.messagesInput) {
      this.messagesInput.registerOnChange(this.onMessagesChange);
      this.messagesInput.writeValue(this.headerMessageConfig?.messageList.map(v => JSON.stringify(v)) || []);
    }

    // Re-initialize when the parent patches the control externally (e.g. version restore).
    // initializeFormControls() uses setValue without emitEvent:false; the internal change
    // handlers guard against re-entrancy via value equality checks so no loop occurs.
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        const defaultConfig: HeaderMessageConfig = {
          displayOrder: 'sequence',
          switchType: 'auto',
          autoSwitchInterval: 5,
          messageList: [],
        };
        this.headerMessageConfig = value && typeof value === 'object'
          ? { ...defaultConfig, ...(value as Partial<HeaderMessageConfig>), messageList: Array.isArray((value as any).messageList) ? (value as any).messageList : [] }
          : defaultConfig;
        this.initializeFormControls();
        if (this.messagesInput) {
          // Pre-clear to force DynamicFormInputComponent to recreate child
          // components when the array length is the same (same-length branch
          // patches in-place with emitEvent:false, so children never re-init).
          this.messagesInput.writeValue([]);
          this.messagesInput.writeValue(
            this.headerMessageConfig.messageList.map(v => JSON.stringify(v)),
          );
        }
        this.changeDetector.markForCheck();
      });

    this.changeDetector.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFormControls() {
    if (!this.headerMessageConfig) return;

    this.displayOrderControl.setValue(this.headerMessageConfig.displayOrder);
    this.switchTypeControl.setValue(this.headerMessageConfig.switchType);
    this.autoSwitchIntervalControl.setValue(this.headerMessageConfig.autoSwitchInterval);
    this.messagesControl.setValue(this.headerMessageConfig.messageList.map(v => JSON.stringify(v)));
  }


  private onDisplayOrderChange = (value: HeaderMessageDisplayOrderType) => {
    if (this.headerMessageConfig && this.headerMessageConfig.displayOrder !== value) {
      this.headerMessageConfig.displayOrder = value;
      this.updateValue();
    }
  };

  private onSwitchTypeChange = (value: HeaderMessageSwitchType) => {
    if (this.headerMessageConfig && this.headerMessageConfig.switchType !== value) {
      this.headerMessageConfig.switchType = value;
      this.updateValue();
    }
  };

  private onAutoSwitchIntervalChange = (value: number) => {
    if (this.headerMessageConfig && this.headerMessageConfig.autoSwitchInterval !== value) {
      this.headerMessageConfig.autoSwitchInterval = value;
      this.updateValue();
    }
  };

  private onMessagesChange = (value: string[] | null) => {
    const parsed = (value || [])
      .map(v => {
        try {
          return JSON.parse(v) as TextValue;
        } catch {
          return null;
        }
      })
      .filter((item): item is TextValue => !!item);

    if (this.headerMessageConfig && JSON.stringify(this.headerMessageConfig.messageList) !== JSON.stringify(parsed)) {
      this.headerMessageConfig.messageList = parsed;
      this.updateValue();
    }
  };

  private updateValue() {
    if (!this.headerMessageConfig) return;
    this.formControl.setValue(this.headerMessageConfig, { emitEvent: false });
    this.formControl.markAsDirty();
  }
}
